import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  timeout: 12000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('elo_access');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(res => res, async (error) => {
  const orig = error.config;
  if (error.response?.status === 401 && !orig._retry) {
    orig._retry = true;
    try {
      const refresh = localStorage.getItem('elo_refresh');
      if (!refresh) throw new Error('no refresh');
      const { data } = await axios.post(`${API_BASE}/auth/token/refresh/`, { refresh });
      localStorage.setItem('elo_access', data.access);
      orig.headers.Authorization = `Bearer ${data.access}`;
      return api(orig);
    } catch {
      localStorage.removeItem('elo_access');
      localStorage.removeItem('elo_refresh');
      window.location.href = '/login';
    }
  }
  return Promise.reject(error);
});

export const authAPI = {
  login:    d  => api.post('/auth/login/', d),
  logout:   d  => api.post('/auth/logout/', d),
  profile:  () => api.get('/auth/profile/'),
  teachers: (school_id) => api.get(`/auth/teachers/?school_id=${school_id || ''}`),
};

export const obsAPI = {
  list:      (params = {}) => api.get('/observations/', { params }),
  start:     d      => api.post('/observations/start/', d),
  get:       id     => api.get(`/observations/${id}/`),
  update:    (id,d) => api.patch(`/observations/${id}/`, d),
  complete:  id     => api.post(`/observations/${id}/complete/`),
  join:      code   => api.post('/observations/join/', { session_code: code }),
  reserveCode: ()   => api.post('/observations/session-code/'),
  participants: id  => api.get(`/observations/${id}/participants/`),
  transferLead: (id, userId) => api.post(`/observations/${id}/transfer-lead/`, { user_id: userId }),
  reconcile:    id  => api.get(`/observations/${id}/reconcile/`),
  mergeNote:    (id, into) => api.post(`/observations/notes/${id}/merge/`, { into }),
  unmergeNote:  id  => api.post(`/observations/notes/${id}/unmerge/`),
  resolveDivergence: (id, resolution) =>
    api.post(`/observations/divergences/${id}/resolve/`, { resolution }),
  addNote:    d      => api.post('/observations/notes/', d),
  updateNote: (id,d) => api.patch(`/observations/notes/${id}/`, d),
  // The note gate, without saving — called as the observer leaves a field.
  checkNote:  d      => api.post('/observations/notes/check/', d),
  notes:     id     => api.get(`/observations/${id}/notes/`),
  dashboard: (params = {}) => api.get('/observations/dashboard/', { params }),
  buckets:       () => api.get('/observations/buckets/'),
  suggestBucket: d  => api.post('/observations/suggest-bucket/', d),
  statements: id => api.post(`/observations/${id}/statements/`),
  updateStatement: (id, statement) =>
    api.patch(`/observations/statements/${id}/`, { statement }),
  strengthenImpact: noteId => api.post(`/observations/notes/${noteId}/strengthen-impact/`),
  getDistricts: () => api.get('/schools/districts/'),
  getSchools: (districtId) =>
    api.get('/schools/', { params: districtId ? { district_id: districtId } : {} }),
  getTeachers: (schoolId) => api.get('/auth/teachers/', { params: { school_id: schoolId } }),
};

export const debriefAPI = {
  create: d      => api.post('/debriefs/', d),
  get:    id     => api.get(`/debriefs/${id}/`),
  update: (id,d) => api.patch(`/debriefs/${id}/`, d),
};

export const feedbackAPI = {
  send: d  => api.post('/feedback/send/', d),
  list: () => api.get('/feedback/'),
};

export const adminAPI = {
  overview:    ()        => api.get('/admin/overview/'),
  options:     ()        => api.get('/admin/options/'),

  users:       (params={}) => api.get('/admin/users/', { params }),
  createUser:  d         => api.post('/admin/users/', d),
  updateUser:  (id, d)   => api.patch(`/admin/users/${id}/`, d),
  assignments:     id    => api.get(`/admin/users/${id}/assignments/`),
  setAssignments: (id,d) => api.put(`/admin/users/${id}/assignments/`, d),

  roles:        ()       => api.get('/admin/roles/'),
  permissions:  ()       => api.get('/admin/permissions/'),
  setRolePerms: (id, codes) =>
    api.put(`/admin/roles/${id}/permissions/`, { permission_codes: codes }),

  districts:      ()     => api.get('/admin/districts/'),
  createDistrict: d      => api.post('/admin/districts/', d),
  schools:        (params={}) => api.get('/admin/schools/', { params }),
  createSchool:   d      => api.post('/admin/schools/', d),
  updateSchool:   (id,d) => api.patch(`/admin/schools/${id}/`, d),

  observations:  (params={}) => api.get('/admin/observations/', { params }),
  schoolDashboard:   (id, params={}) => api.get(`/admin/dashboard/school/${id}/`, { params }),
  districtDashboard: (id, params={}) => api.get(`/admin/dashboard/district/${id}/`, { params }),
  setFocus:      (id, bucket) => api.post(`/admin/schools/${id}/focus/`, { focus_bucket: bucket }),
  exportUrl:     (kind, params={}) => {
    const qs = new URLSearchParams(params).toString();
    return `/admin/exports/${kind}/${qs ? `?${qs}` : ''}`;
  },
  download:      (path) => api.get(path, { responseType: 'blob' }),

  patches:       (params={}) => api.get('/admin/patches/', { params }),
  createPatch:   d       => api.post('/admin/patches/', d),
  updatePatch:   (id,d)  => api.patch(`/admin/patches/${id}/`, d),
  deletePatch:   id      => api.delete(`/admin/patches/${id}/`),
};

export const schoolsAPI = {
  list:     () => api.get('/schools/'),
  get:      id => api.get(`/schools/${id}/`),
  setFocus: (id, bucket) => api.post(`/schools/${id}/focus/`, { focus_bucket: bucket }),
};

export default api;
